/**
 * Publisher session pseudonym generator
 *
 * Formula: pseudonym = hashToCurve(scope) · nymSecret
 *   where scope = lengthPrefixedConcat(DST, publisher_id, nonce)
 *
 * Length-prefixing prevents encoding collisions where different
 * (publisher_id, nonce) splits would produce the same scope bytes.
 *
 * Uses BLS12-381 G1 via CryptoSuite (Approach B from 18a spike).
 * Pseudonym is 48 bytes (compressed G1 point).
 *
 * @traces R4.1
 */
import { lengthPrefixedConcat, utf8 } from '../crypto/encoding.js';
const PSEUDONYM_DST = utf8('OwnYou-Pseudonym-v1');
/**
 * Build the hash-to-curve scope bytes for a publisher session pseudonym.
 * Length-prefixed so no two (publisherId, nonce) pairs collide.
 */
export function pseudonymScope(publisherId, nonce) {
    if (typeof publisherId !== 'string') {
        throw new TypeError('pseudonymScope: publisherId must be string');
    }
    if (typeof nonce !== 'string') {
        throw new TypeError('pseudonymScope: nonce must be string');
    }
    return lengthPrefixedConcat([PSEUDONYM_DST, utf8(publisherId), utf8(nonce)]);
}
/**
 * Generate a publisher session pseudonym.
 *
 * @param suite - CryptoSuite implementation
 * @param nymSecret - 32-byte nymSecret from KeyDerivation.deriveNymSecret()
 * @param publisherId - Publisher identifier string
 * @param nonce - Session nonce (e.g., from publisher's bid request)
 * @returns 48-byte compressed G1 point
 *
 * @traces R4.1
 */
export function generatePseudonym(suite, nymSecret, publisherId, nonce) {
    if (!(nymSecret instanceof Uint8Array) || nymSecret.length !== 32) {
        throw new TypeError('generatePseudonym: nymSecret must be 32-byte Uint8Array');
    }
    const basePoint = suite.hashToCurve(pseudonymScope(publisherId, nonce));
    return suite.scalarMultiply(basePoint, nymSecret);
}
